import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Clock, Download, Phone, Mail, Sparkles, Utensils, Wine, Music, Camera, PartyPopper, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const GreinerLiptalOfferPage = () => {
  const [guestCount, setGuestCount] = useState(350);
  
  // Base overheads and personnel for 450 guests
  const BASE_OVERHEADS = {
    jednorObaly: 1000,
    foodTruck: 2,
    mobilniBar: 1,
    beerTruck: 1,
    nuzkoveStany: 2,
    nivtecStoly: 6,
    dopravaCatering: 1,
    dopravaPersonal: 1,
    dopravaFoodTruck: 1,
    dopravaBeerBar: 1,
    likvidaceOdpadu: 1,
    uklidoveProstredky: 1,
    prebraniArealu: 1,
    pronajemArealu: 1
  };
  
  const OVERHEADS_PRICES = {
    jednorObaly: 12,
    foodTruck: 5000,
    mobilniBar: 5000,
    beerTruck: 5000,
    nuzkoveStany: 2000,
    nivtecStoly: 400,
    dopravaCatering: 2000,
    dopravaPersonal: 2000,
    dopravaFoodTruck: 5000,
    dopravaBeerBar: 2000,
    likvidaceOdpadu: 4000,
    uklidoveProstredky: 4500,
    prebraniArealu: 4000,
    pronajemArealu: 8000
  };
  
  const BASE_PERSONNEL = {
    kuchar: { count: 2, hours: 12, rate: 290 },
    pomocnyKuchar: { count: 2, hours: 12, rate: 250 },
    barmani: { count: 4, hours: 12, rate: 250 },
    barmaniKoktejly: { count: 2, hours: 12, rate: 250 },
    debaras: { count: 2, hours: 12, rate: 200 },
    ostraha: { count: 2, hours: 9, rate: 350 }
  };
  
  const [overheads, setOverheads] = useState(BASE_OVERHEADS);
  const [personnel, setPersonnel] = useState(BASE_PERSONNEL);
  
  // Base values for 450 guests (reference for proportional calculation)
  const BASE_GUESTS = 450;
  const BASE_CATERING = {
    beefBurger: 150, pulledHotdog: 154, tortilla: 193, krkovicka: 200,
    kureciPrso: 257, zebirka: 193, hermelin: 50, peperovaOmacka: 200,
    houbovaOmacka: 250, tatarskaOmacka: 200, zeleneLusky: 150, brambory: 450,
    zelenina: 200, kukurice: 100, kureciRizecky: 386, veprovyRizecky: 257,
    coleslaw: 100, sopskySalat: 250, pecivo: 600, studeneOmacky: 100,
    zeleninka: 100, dezerty: 450
  };
  const BASE_DRINKS = {
    prosecco: 900, koktejly: 450, vino: 225, radegast: 300,
    prazdroj: 300, birell: 240, kofola: 200, voda: 150, kava: 200
  };
  
  // Calculate proportional value
  const calcProp = (baseValue) => Math.round((baseValue / BASE_GUESTS) * guestCount);
  
  // Catering & Drinks (will be recalculated when guestCount changes)
  const [catering, setCatering] = useState(BASE_CATERING);
  const [drinks, setDrinks] = useState(BASE_DRINKS);
  
  // Inventory quantities (base for 450 guests)
  const BASE_INVENTORY = {
    stan8x4: 6,
    pivniSet: 25,
    doprava: 2,
    osvetleni: 8,
    koktejlovyStul: 20,
    identifikacniPasky: 450,
    montaz: 1,
    ozvuceniPodium: 1,
    technickyKviz: 1
  };
  
  const [inventory, setInventory] = useState(BASE_INVENTORY);

  const EVENT_DATE = '19. června 2026';
  const EVENT_TIME = '15:00 - 23:00';
  const VENUE = 'Venkovní areál Liptál';
  
  // Catering prices (Kč/kus)
  const cateringPrices = {
    beefBurger: 210,
    pulledHotdog: 149,
    tortilla: 210,
    krkovicka: 179,
    kureciPrso: 139,
    zebirka: 189,
    hermelin: 159,
    peperovaOmacka: 45,
    houbovaOmacka: 45,
    tatarskaOmacka: 25,
    zeleneLusky: 45,
    brambory: 45,
    zelenina: 50,
    kukurice: 55,
    kureciRizecky: 55,
    veprovyRizecky: 65,
    coleslaw: 30,
    sopskySalat: 30,
    pecivo: 15,
    studeneOmacky: 29,
    zeleninka: 19,
    dezerty: 99
  };
  
  // Inventory prices (Kč/kus)
  const inventoryPrices = {
    stan8x4: 3500,
    pivniSet: 250,
    doprava: 2700,
    osvetleni: 250,
    koktejlovyStul: 350,
    identifikacniPasky: 5,
    montaz: 5000,
    ozvuceniPodium: 10000,
    technickyKviz: 8000
  };
  
  // Inventory discount (20%)
  const INVENTORY_DISCOUNT = 0.20;
  
  // Drinks prices (Kč/kus)
  const drinksPrices = {
    prosecco: 50,
    koktejly: 100,
    vino: 45,
    radegast: 45,
    prazdroj: 55,
    birell: 45,
    kofola: 50,
    voda: 30,
    kava: 40
  };
  
  // Calculate overheads total
  const calculateOverheadsTotal = () => {
    let total = 0;
    Object.keys(overheads).forEach(key => {
      total += overheads[key] * OVERHEADS_PRICES[key];
    });
    return total;
  };
  
  // Calculate personnel total
  const calculatePersonnelTotal = () => {
    let total = 0;
    Object.keys(personnel).forEach(key => {
      const p = personnel[key];
      total += p.count * p.hours * p.rate;
    });
    return total;
  };
  
  const overheadsTotal = calculateOverheadsTotal();
  const personnelTotal = calculatePersonnelTotal();
  
  // Fixed costs
  // Program and Production (fixed costs)
  const PROGRAM = 51000; // DJ ZAX 10000 + AI Fotkoutek 20000 (vč. zastřešení 8×4m) + Chytrý kvíz 21000 (includes 8000 technical realization)
  const PRODUCTION = 8600; // 20% z celkového programu (43000 * 0.20)
  
  const updateCatering = (item, value) => {
    setCatering(prev => ({ ...prev, [item]: Math.max(0, parseInt(value) || 0) }));
  };
  
  const updateDrinks = (item, value) => {
    setDrinks(prev => ({ ...prev, [item]: Math.max(0, parseInt(value) || 0) }));
  };
  
  const updateInventory = (item, value) => {
    setInventory(prev => ({ ...prev, [item]: Math.max(0, parseInt(value) || 0) }));
  };
  
  // Update guest count and recalculate all proportionally
  const updateGuestCount = (newCount) => {
    const count = Math.max(1, Math.min(1000, newCount)); // Min 1, Max 1000
    setGuestCount(count);
    
    // Recalculate catering proportionally
    const newCatering = {};
    Object.keys(BASE_CATERING).forEach(key => {
      newCatering[key] = Math.round((BASE_CATERING[key] / BASE_GUESTS) * count);
    });
    setCatering(newCatering);
    
    // Recalculate drinks proportionally
    // Pivo (radegast, prazdroj, birell) zaokrouhlujeme nahoru na násobek 60 (sud).
    // Kofola: nahoru na násobek 100 (KEG).
    const ROUND_UP_STEPS = { radegast: 60, prazdroj: 60, birell: 60, kofola: 100 };
    const ceilTo = (value, step) => Math.ceil(value / step) * step;
    const newDrinks = {};
    Object.keys(BASE_DRINKS).forEach(key => {
      const proportional = (BASE_DRINKS[key] / BASE_GUESTS) * count;
      const step = ROUND_UP_STEPS[key];
      newDrinks[key] = step ? ceilTo(proportional, step) : Math.round(proportional);
    });
    setDrinks(newDrinks);
    
    // Inventory (technické vybavení) je převážně FIXNÍ — nezávislé na počtu hostů.
    // Výjimka: identifikační pásky na ruce — 1 ks na hosta.
    setInventory({ ...BASE_INVENTORY, identifikacniPasky: count });
    
    // Recalculate overheads - ONLY jednorObaly is proportional, rest is FIXED
    const newOverheads = { ...BASE_OVERHEADS };
    newOverheads.jednorObaly = Math.round((BASE_OVERHEADS.jednorObaly / BASE_GUESTS) * count);
    setOverheads(newOverheads);
    
    // Recalculate personnel - ONLY barmani and barmaniKoktejly are proportional, rest is FIXED
    const newPersonnel = {
      kuchar: { ...BASE_PERSONNEL.kuchar },
      pomocnyKuchar: { ...BASE_PERSONNEL.pomocnyKuchar },
      barmani: {
        count: Math.round((BASE_PERSONNEL.barmani.count / BASE_GUESTS) * count),
        hours: BASE_PERSONNEL.barmani.hours,
        rate: BASE_PERSONNEL.barmani.rate
      },
      barmaniKoktejly: {
        count: Math.round((BASE_PERSONNEL.barmaniKoktejly.count / BASE_GUESTS) * count),
        hours: BASE_PERSONNEL.barmaniKoktejly.hours,
        rate: BASE_PERSONNEL.barmaniKoktejly.rate
      },
      debaras: { ...BASE_PERSONNEL.debaras },
      ostraha: { ...BASE_PERSONNEL.ostraha }
    };
    setPersonnel(newPersonnel);
  };

  // On mount, initialize all proportional calculations for the initial guestCount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { updateGuestCount(guestCount); }, []);
  
  // Calculations
  const cateringTotal = Object.keys(catering).reduce((sum, key) => sum + catering[key] * cateringPrices[key], 0);
  const drinksTotal = Object.keys(drinks).reduce((sum, key) => sum + drinks[key] * drinksPrices[key], 0);
  
  // Calculate volume discount for food & drinks (degressive 0-10%)
  // 250 guests = 0%, 450 guests = 10%, linear interpolation between
  const calculateVolumeDiscount = () => {
    if (guestCount <= 250) return 0;
    if (guestCount >= 450) return 0.10;
    return ((guestCount - 250) / (450 - 250)) * 0.10;
  };
  
  const volumeDiscountPercent = calculateVolumeDiscount();
  const foodDrinksSubtotal = cateringTotal + drinksTotal;
  const volumeDiscountAmount = foodDrinksSubtotal * volumeDiscountPercent;
  const foodDrinksTotalAfterDiscount = foodDrinksSubtotal - volumeDiscountAmount;
  
  // Inventory total with 20% discount
  const inventorySubtotal = 
    inventory.stan8x4 * inventoryPrices.stan8x4 + 
    inventory.pivniSet * inventoryPrices.pivniSet +
    inventory.doprava * inventoryPrices.doprava +
    inventory.osvetleni * inventoryPrices.osvetleni +
    (inventory.ozvuceniPodium || 1) * inventoryPrices.ozvuceniPodium +
    (inventory.technickyKviz || 1) * inventoryPrices.technickyKviz +
    inventory.montaz * inventoryPrices.montaz;
  const inventoryDiscount = inventorySubtotal * INVENTORY_DISCOUNT;
  const inventoryTotal = inventorySubtotal - inventoryDiscount;

  // Partner discount – 20 000 Kč při objednávce 350+ osob
  const PARTNER_DISCOUNT_THRESHOLD = 350;
  const PARTNER_DISCOUNT_AMOUNT = 20000;
  const partnerDiscountActive = guestCount >= PARTNER_DISCOUNT_THRESHOLD;
  const partnerDiscount = partnerDiscountActive ? PARTNER_DISCOUNT_AMOUNT : 0;

  const grandTotalBeforePartner = foodDrinksTotalAfterDiscount + overheadsTotal + personnelTotal + PROGRAM + PRODUCTION + inventoryTotal;
  const grandTotal = grandTotalBeforePartner - partnerDiscount;

  // PDF Export
  const handleDownloadPDF = () => {
    const cateringItems = [
      ...(catering.beefBurger > 0 ? [['  • Beef burger (' + catering.beefBurger + '× á 210 Kč)', (catering.beefBurger * cateringPrices.beefBurger).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.pulledHotdog > 0 ? [['  • Pulled hot-dog (' + catering.pulledHotdog + '× á 149 Kč)', (catering.pulledHotdog * cateringPrices.pulledHotdog).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.tortilla > 0 ? [['  • Tortila se zeleninou (' + catering.tortilla + '× á 210 Kč)', (catering.tortilla * cateringPrices.tortilla).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.krkovicka > 0 ? [['  • Grilovaná krkovička (' + catering.krkovicka + '× á 179 Kč)', (catering.krkovicka * cateringPrices.krkovicka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.kureciPrso > 0 ? [['  • Grilované kuřecí prso (' + catering.kureciPrso + '× á 139 Kč)', (catering.kureciPrso * cateringPrices.kureciPrso).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.zebirka > 0 ? [['  • Vepřové žebra BBQ (' + catering.zebirka + '× á 189 Kč)', (catering.zebirka * cateringPrices.zebirka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.hermelin > 0 ? [['  • Grilovaný hermelín (' + catering.hermelin + '× á 159 Kč)', (catering.hermelin * cateringPrices.hermelin).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.peperovaOmacka > 0 ? [['  • Pepřová omáčka (' + catering.peperovaOmacka + '× á 45 Kč)', (catering.peperovaOmacka * cateringPrices.peperovaOmacka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.houbovaOmacka > 0 ? [['  • Houbová omáčka (' + catering.houbovaOmacka + '× á 45 Kč)', (catering.houbovaOmacka * cateringPrices.houbovaOmacka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.tatarskaOmacka > 0 ? [['  • Tatarská omáčka (' + catering.tatarskaOmacka + '× á 25 Kč)', (catering.tatarskaOmacka * cateringPrices.tatarskaOmacka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.zeleneLusky > 0 ? [['  • Pečené zelené lusky (' + catering.zeleneLusky + '× á 45 Kč)', (catering.zeleneLusky * cateringPrices.zeleneLusky).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.brambory > 0 ? [['  • Pečené brambory grenaille (' + catering.brambory + '× á 45 Kč)', (catering.brambory * cateringPrices.brambory).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.zelenina > 0 ? [['  • Grilovaná zelenina (' + catering.zelenina + '× á 50 Kč)', (catering.zelenina * cateringPrices.zelenina).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.kukurice > 0 ? [['  • Grilovaná kukuřice (' + catering.kukurice + '× á 55 Kč)', (catering.kukurice * cateringPrices.kukurice).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.kureciRizecky > 0 ? [['  • Kuřecí banketní řízečky (' + catering.kureciRizecky + '× á 55 Kč)', (catering.kureciRizecky * cateringPrices.kureciRizecky).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.veprovyRizecky > 0 ? [['  • Vepřové řízečky (' + catering.veprovyRizecky + '× á 65 Kč)', (catering.veprovyRizecky * cateringPrices.veprovyRizecky).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.coleslaw > 0 ? [['  • Zelný salát coleslaw (' + catering.coleslaw + '× á 30 Kč)', (catering.coleslaw * cateringPrices.coleslaw).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.sopskySalat > 0 ? [['  • Šopský salát (' + catering.sopskySalat + '× á 30 Kč)', (catering.sopskySalat * cateringPrices.sopskySalat).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.pecivo > 0 ? [['  • Koš pečiva (' + catering.pecivo + '× á 15 Kč)', (catering.pecivo * cateringPrices.pecivo).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.studeneOmacky > 0 ? [['  • Studené omáčky (' + catering.studeneOmacky + '× á 29 Kč)', (catering.studeneOmacky * cateringPrices.studeneOmacky).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.zeleninka > 0 ? [['  • Sterilizovaná zelenina (' + catering.zeleninka + '× á 19 Kč)', (catering.zeleninka * cateringPrices.zeleninka).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(catering.dezerty > 0 ? [['  • Dezerty v kelímku (' + catering.dezerty + '× á 99 Kč)', (catering.dezerty * cateringPrices.dezerty).toLocaleString('cs-CZ') + ' Kč']] : [])
    ];
    
    const drinksItems = [
      ...(drinks.prosecco > 0 ? [['  • Točené prosecco (' + drinks.prosecco + '× á 50 Kč)', (drinks.prosecco * drinksPrices.prosecco).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.koktejly > 0 ? [['  • Míchané koktejly (' + drinks.koktejly + '× á 100 Kč)', (drinks.koktejly * drinksPrices.koktejly).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.vino > 0 ? [['  • Sudové víno (' + drinks.vino + '× á 45 Kč)', (drinks.vino * drinksPrices.vino).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.radegast > 0 ? [['  • Pivo Radegast (' + drinks.radegast + '× á 45 Kč)', (drinks.radegast * drinksPrices.radegast).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.prazdroj > 0 ? [['  • Pivo Prazdroj (' + drinks.prazdroj + '× á 55 Kč)', (drinks.prazdroj * drinksPrices.prazdroj).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.birell > 0 ? [['  • Točený Birell (' + drinks.birell + '× á 45 Kč)', (drinks.birell * drinksPrices.birell).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.kofola > 0 ? [['  • Točená kofola (' + drinks.kofola + '× á 50 Kč)', (drinks.kofola * drinksPrices.kofola).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.voda > 0 ? [['  • Voda PET (' + drinks.voda + '× á 30 Kč)', (drinks.voda * drinksPrices.voda).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(drinks.kava > 0 ? [['  • Prémiová káva (' + drinks.kava + '× á 40 Kč)', (drinks.kava * drinksPrices.kava).toLocaleString('cs-CZ') + ' Kč']] : [])
    ];

    // Overheads items with updated names
    const overheadsItems = [
      ['  • Jednorázové obaly a příbory (' + overheads.jednorObaly + '× á ' + OVERHEADS_PRICES.jednorObaly + ' Kč)', (overheads.jednorObaly * OVERHEADS_PRICES.jednorObaly).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Food truck pronájem (' + overheads.foodTruck + '× á ' + OVERHEADS_PRICES.foodTruck.toLocaleString('cs-CZ') + ' Kč)', (overheads.foodTruck * OVERHEADS_PRICES.foodTruck).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Bar Truck XXL (' + overheads.mobilniBar + '× á ' + OVERHEADS_PRICES.mobilniBar.toLocaleString('cs-CZ') + ' Kč)', (overheads.mobilniBar * OVERHEADS_PRICES.mobilniBar).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Beer truck (' + overheads.beerTruck + '× á ' + OVERHEADS_PRICES.beerTruck.toLocaleString('cs-CZ') + ' Kč)', (overheads.beerTruck * OVERHEADS_PRICES.beerTruck).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Nůžkové stany 6x3m pro catering (' + overheads.nuzkoveStany + '× á ' + OVERHEADS_PRICES.nuzkoveStany.toLocaleString('cs-CZ') + ' Kč)', (overheads.nuzkoveStany * OVERHEADS_PRICES.nuzkoveStany).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Zátěžové výdejní stoly (' + overheads.nivtecStoly + '× á ' + OVERHEADS_PRICES.nivtecStoly + ' Kč)', (overheads.nivtecStoly * OVERHEADS_PRICES.nivtecStoly).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Doprava catering (' + overheads.dopravaCatering + '× á ' + OVERHEADS_PRICES.dopravaCatering.toLocaleString('cs-CZ') + ' Kč)', (overheads.dopravaCatering * OVERHEADS_PRICES.dopravaCatering).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Doprava personál (' + overheads.dopravaPersonal + '× á ' + OVERHEADS_PRICES.dopravaPersonal.toLocaleString('cs-CZ') + ' Kč)', (overheads.dopravaPersonal * OVERHEADS_PRICES.dopravaPersonal).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Doprava food truck (' + overheads.dopravaFoodTruck + '× á ' + OVERHEADS_PRICES.dopravaFoodTruck.toLocaleString('cs-CZ') + ' Kč)', (overheads.dopravaFoodTruck * OVERHEADS_PRICES.dopravaFoodTruck).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Doprava beer bar (' + overheads.dopravaBeerBar + '× á ' + OVERHEADS_PRICES.dopravaBeerBar.toLocaleString('cs-CZ') + ' Kč)', (overheads.dopravaBeerBar * OVERHEADS_PRICES.dopravaBeerBar).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Likvidace odpadu (' + overheads.likvidaceOdpadu + '× á ' + OVERHEADS_PRICES.likvidaceOdpadu.toLocaleString('cs-CZ') + ' Kč)', (overheads.likvidaceOdpadu * OVERHEADS_PRICES.likvidaceOdpadu).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Nákup úklidových prostředků a jejich distribuce (' + overheads.uklidoveProstredky + '× á ' + OVERHEADS_PRICES.uklidoveProstredky.toLocaleString('cs-CZ') + ' Kč)', (overheads.uklidoveProstredky * OVERHEADS_PRICES.uklidoveProstredky).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Přebrání a předání areálu – 4× cesta Otrokovice–Liptál + čas odpovědné osoby (' + overheads.prebraniArealu + '× á ' + OVERHEADS_PRICES.prebraniArealu.toLocaleString('cs-CZ') + ' Kč)', (overheads.prebraniArealu * OVERHEADS_PRICES.prebraniArealu).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Pronájem areálu (' + overheads.pronajemArealu + '× á ' + OVERHEADS_PRICES.pronajemArealu.toLocaleString('cs-CZ') + ' Kč)', (overheads.pronajemArealu * OVERHEADS_PRICES.pronajemArealu).toLocaleString('cs-CZ') + ' Kč']
    ];

    // Personnel items with updated names
    const personnelItems = [
      ['  • Kuchař (' + personnel.kuchar.count + ' os. × ' + personnel.kuchar.hours + ' hod × ' + personnel.kuchar.rate + ' Kč)', (personnel.kuchar.count * personnel.kuchar.hours * personnel.kuchar.rate).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Pomocný kuchař (' + personnel.pomocnyKuchar.count + ' os. × ' + personnel.pomocnyKuchar.hours + ' hod × ' + personnel.pomocnyKuchar.rate + ' Kč)', (personnel.pomocnyKuchar.count * personnel.pomocnyKuchar.hours * personnel.pomocnyKuchar.rate).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Barmani - výčepní (' + personnel.barmani.count + ' os. × ' + personnel.barmani.hours + ' hod × ' + personnel.barmani.rate + ' Kč)', (personnel.barmani.count * personnel.barmani.hours * personnel.barmani.rate).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Barmani - koktejly (' + personnel.barmaniKoktejly.count + ' os. × ' + personnel.barmaniKoktejly.hours + ' hod × ' + personnel.barmaniKoktejly.rate + ' Kč)', (personnel.barmaniKoktejly.count * personnel.barmaniKoktejly.hours * personnel.barmaniKoktejly.rate).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Debaras / úklid (' + personnel.debaras.count + ' os. × ' + personnel.debaras.hours + ' hod × ' + personnel.debaras.rate + ' Kč)', (personnel.debaras.count * personnel.debaras.hours * personnel.debaras.rate).toLocaleString('cs-CZ') + ' Kč'],
      ['  • Ostraha areálu (' + personnel.ostraha.count + ' os. × ' + personnel.ostraha.hours + ' hod × ' + personnel.ostraha.rate + ' Kč)', (personnel.ostraha.count * personnel.ostraha.hours * personnel.ostraha.rate).toLocaleString('cs-CZ') + ' Kč']
    ];

    // Inventory items with new technical items
    const inventoryItems = [
      ...(inventory.stan8x4 > 0 ? [['  • Stan 8×4m (' + inventory.stan8x4 + '× á ' + inventoryPrices.stan8x4.toLocaleString('cs-CZ') + ' Kč)', (inventory.stan8x4 * inventoryPrices.stan8x4).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(inventory.pivniSet > 0 ? [['  • Pivní set (' + inventory.pivniSet + '× á ' + inventoryPrices.pivniSet + ' Kč)', (inventory.pivniSet * inventoryPrices.pivniSet).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(inventory.doprava > 0 ? [['  • Doprava (' + inventory.doprava + '× á ' + inventoryPrices.doprava.toLocaleString('cs-CZ') + ' Kč)', (inventory.doprava * inventoryPrices.doprava).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(inventory.osvetleni > 0 ? [['  • Osvětlení do stanů (' + inventory.osvetleni + '× á ' + inventoryPrices.osvetleni + ' Kč)', (inventory.osvetleni * inventoryPrices.osvetleni).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(inventory.koktejlovyStul > 0 ? [['  • Koktejlový stůl vč. potahu (' + inventory.koktejlovyStul + '× á ' + inventoryPrices.koktejlovyStul + ' Kč)', (inventory.koktejlovyStul * inventoryPrices.koktejlovyStul).toLocaleString('cs-CZ') + ' Kč']] : []),
      ...(inventory.identifikacniPasky > 0 ? [['  • Identifikační pásky na ruce (' + inventory.identifikacniPasky + '× á ' + inventoryPrices.identifikacniPasky + ' Kč)', (inventory.identifikacniPasky * inventoryPrices.identifikacniPasky).toLocaleString('cs-CZ') + ' Kč']] : []),
      ['  • Ozvučení a osvětlení pódia (fixní)', inventoryPrices.ozvuceniPodium.toLocaleString('cs-CZ') + ' Kč'],
      ['  • Technická realizace chytrého kvízu (fixní)', inventoryPrices.technickyKviz.toLocaleString('cs-CZ') + ' Kč'],
      ...(inventory.montaz > 0 ? [['  • Montáž / Demontáž (fixní)', (inventory.montaz * inventoryPrices.montaz).toLocaleString('cs-CZ') + ' Kč']] : [])
    ];

    const docDefinition = {
      content: [
        { text: 'Greiner packaging - Venkovní areál Liptál', style: 'header', alignment: 'center', margin: [0, 0, 0, 5] },
        { text: 'Orientační rozpočet', style: 'subheader', alignment: 'center', margin: [0, 0, 0, 20] },
        {
          text: [
            { text: 'Datum: ', bold: true }, EVENT_DATE + '  •  ',
            { text: 'Čas: ', bold: true }, EVENT_TIME + '  •  ',
            { text: 'Počet osob: ', bold: true }, `${guestCount}  •  `,
            { text: 'Lokalita: ', bold: true }, VENUE
          ],
          alignment: 'center',
          margin: [0, 0, 0, 20],
          fontSize: 10
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 100],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#dc2626', color: '#ffffff' },
                { text: 'Cena', style: 'tableHeader', fillColor: '#dc2626', color: '#ffffff', alignment: 'right' }
              ],
              [{ text: 'CATERING', bold: true, fillColor: '#fee2e2', colSpan: 2 }, {}],
              ...cateringItems,
              [{ text: 'Celkem Catering', bold: true, fillColor: '#fef3c7' }, { text: cateringTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              [{ text: 'NÁPOJE', bold: true, fillColor: '#fee2e2', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ...drinksItems,
              [{ text: 'Celkem Nápoje', bold: true, fillColor: '#fef3c7' }, { text: drinksTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              ...(volumeDiscountAmount > 0 ? [
                [{ text: 'Mezisoučet Jídlo + Nápoje', fillColor: '#fef3c7' }, { text: foodDrinksSubtotal.toLocaleString('cs-CZ') + ' Kč', fillColor: '#fef3c7', alignment: 'right' }],
                [{ text: `Objemová sleva (${Math.round(volumeDiscountPercent * 100)}% pro ${guestCount} osob)`, fillColor: '#fef3c7', color: '#15803d' }, { text: `-${volumeDiscountAmount.toLocaleString('cs-CZ')} Kč`, fillColor: '#fef3c7', alignment: 'right', color: '#15803d' }],
                [{ text: 'Celkem Jídlo + Nápoje po slevě', bold: true, fillColor: '#bbf7d0' }, { text: foodDrinksTotalAfterDiscount.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#bbf7d0', alignment: 'right' }]
              ] : []),
              [{ text: 'REŽIE', bold: true, fillColor: '#e0f2fe', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ...overheadsItems,
              [{ text: 'Celkem Režie', bold: true, fillColor: '#bfdbfe' }, { text: overheadsTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#bfdbfe', alignment: 'right' }],
              [{ text: 'PERSONÁL', bold: true, fillColor: '#e5e7eb', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ...personnelItems,
              [{ text: 'Celkem Personál', bold: true, fillColor: '#d1d5db' }, { text: personnelTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#d1d5db', alignment: 'right' }],
              [{ text: 'PROGRAM', bold: true, fillColor: '#fee2e2', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • DJ ZAX + Chytrý kvíz + AI Fotkoutek (3h, 19.6.2026 17:00–20:00, vč. zastřešení 8×4 m)', PROGRAM.toLocaleString('cs-CZ') + ' Kč'],
              [{ text: 'PRODUKCE', bold: true, fillColor: '#fee2e2', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Koordinace', PRODUCTION.toLocaleString('cs-CZ') + ' Kč'],
              ...(inventoryTotal > 0 ? [
                [{ text: 'TECHNICKÉ VYBAVENÍ (INVENTÁŘ)', bold: true, fillColor: '#d1fae5', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
                ...inventoryItems,
                [{ text: 'Mezisoučet inventáře', fillColor: '#f0fdf4' }, { text: inventorySubtotal.toLocaleString('cs-CZ') + ' Kč', fillColor: '#f0fdf4', alignment: 'right' }],
                [{ text: 'Sleva 20%', fillColor: '#f0fdf4' }, { text: '-' + inventoryDiscount.toLocaleString('cs-CZ') + ' Kč', fillColor: '#f0fdf4', alignment: 'right', color: '#dc2626' }],
                [{ text: 'Celkem Inventář', bold: true, fillColor: '#d1fae5' }, { text: inventoryTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#d1fae5', alignment: 'right' }]
              ] : []),
              ...(partnerDiscountActive ? [
                [{ text: 'Mezisoučet (bez partnerské slevy)', fillColor: '#f3f4f6' }, { text: grandTotalBeforePartner.toLocaleString('cs-CZ') + ' Kč', fillColor: '#f3f4f6', alignment: 'right' }],
                [{ text: `🤝 Partnerská sleva (350+ osob)`, fillColor: '#dcfce7', color: '#15803d', bold: true }, { text: '-' + PARTNER_DISCOUNT_AMOUNT.toLocaleString('cs-CZ') + ' Kč', fillColor: '#dcfce7', alignment: 'right', color: '#15803d', bold: true }],
              ] : []),
              [
                { text: 'CELKOVÁ CENA (bez DPH)', bold: true, fontSize: 12, fillColor: '#dc2626', color: '#ffffff', margin: [0, 5, 0, 5] },
                { text: grandTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fontSize: 12, fillColor: '#dc2626', color: '#ffffff', alignment: 'right', margin: [0, 5, 0, 5] }
              ],
              ...(() => {
                const cateringAfterDiscount = cateringTotal * (1 - volumeDiscountPercent);
                const drinksAfterDiscount = drinksTotal * (1 - volumeDiscountPercent);
                const base12 = cateringAfterDiscount;
                const base21Items = [
                  { label: 'Nápoje (po slevě)', value: drinksAfterDiscount },
                  { label: 'Režie a tech. vybavení', value: overheadsTotal },
                  { label: 'Personál', value: personnelTotal },
                  { label: 'Program', value: PROGRAM },
                  { label: 'Produkce a koordinace', value: PRODUCTION },
                  ...(inventoryTotal > 0 ? [{ label: 'Inventář', value: inventoryTotal }] : []),
                  ...(partnerDiscountActive ? [{ label: 'Partnerská sleva', value: -partnerDiscount }] : []),
                ];
                const base21 = base21Items.reduce((s, i) => s + i.value, 0);
                const vat12 = base12 * 0.12;
                const vat21 = base21 * 0.21;
                const totalVat = vat12 + vat21;
                const grandTotalWithVat = grandTotal + totalVat;
                const perPersonNoVat = guestCount > 0 ? grandTotal / guestCount : 0;
                const perPersonWithVat = guestCount > 0 ? grandTotalWithVat / guestCount : 0;
                const fmt = (v) => Math.round(v).toLocaleString('cs-CZ') + ' Kč';
                return [
                  [{ text: 'ROZPAD DPH', bold: true, fillColor: '#f3f4f6', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
                  ['  • Jídlo (základ DPH 12 %)', { text: fmt(base12), alignment: 'right' }],
                  [{ text: '  • DPH 12 %', color: '#6b7280' }, { text: '+ ' + fmt(vat12), alignment: 'right', color: '#6b7280' }],
                  ['  • Nápoje a služby (základ DPH 21 %)', { text: fmt(base21), alignment: 'right' }],
                  ...base21Items.map((it) => [
                    { text: '      – ' + it.label, color: '#6b7280' },
                    { text: fmt(it.value), alignment: 'right', color: '#6b7280' },
                  ]),
                  [{ text: '  • DPH 21 %', color: '#6b7280' }, { text: '+ ' + fmt(vat21), alignment: 'right', color: '#6b7280' }],
                  [{ text: 'DPH celkem', bold: true, fillColor: '#fef3c7' }, { text: fmt(totalVat), bold: true, fillColor: '#fef3c7', alignment: 'right' }],
                  [
                    { text: 'CELKOVÁ CENA (s DPH)', bold: true, fontSize: 12, fillColor: '#15803d', color: '#ffffff', margin: [0, 5, 0, 5] },
                    { text: fmt(grandTotalWithVat), bold: true, fontSize: 12, fillColor: '#15803d', color: '#ffffff', alignment: 'right', margin: [0, 5, 0, 5] }
                  ],
                  [{ text: 'CENA NA OSOBU', bold: true, fillColor: '#f3f4f6', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
                  ['  • Cena na osobu (bez DPH)', { text: fmt(perPersonNoVat) + ` (${guestCount} hostů)`, alignment: 'right' }],
                  [
                    { text: '  • Cena na osobu (s DPH)', bold: true },
                    { text: fmt(perPersonWithVat) + ` (${guestCount} hostů)`, bold: true, alignment: 'right', color: '#dc2626' }
                  ]
                ];
              })()
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => '#e5e7eb',
            vLineColor: () => '#e5e7eb'
          }
        },
        {
          text: 'Poznámka: Uvedené ceny jsou orientační a jsou bez DPH. Sazba DPH: jídlo 12 %, nápoje a služby 21 %. Sleva 20 % je aplikována na technické vybavení. Objemová sleva na jídlo a nápoje: 0 % při 250 hostech, až 10 % při 450 hostech (degresivní).',
          margin: [0, 20, 0, 0],
          fontSize: 9,
          italics: true,
          color: '#6b7280'
        }
      ],
      styles: {
        header: { fontSize: 20, bold: true, color: '#dc2626' },
        subheader: { fontSize: 14, color: '#6b7280' },
        tableHeader: { bold: true, fontSize: 11, margin: [0, 5, 0, 5] }
      },
      defaultStyle: { font: 'Roboto', fontSize: 10 }
    };

    pdfMake.createPdf(docDefinition).download(`Greiner_Liptal_${guestCount}os.pdf`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <HeroSection guestCount={guestCount} />
      <InfoSection />
      <ProgramSection />
      
      {/* Guest Count Controller */}
      <section className="py-12 px-4 bg-gradient-to-br from-red-600 via-red-800 to-black text-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center items-center gap-3 mb-4">
              <Users className="w-8 h-8" />
              <h3 className="text-3xl font-bold">Počet hostů</h3>
            </div>
            <p className="text-red-100">Všechny hodnoty se automaticky přepočítají proporcionálně</p>
          </div>
          
          <div className="flex items-center justify-center gap-6">
            <button 
              onClick={() => updateGuestCount(guestCount - 10)}
              className="w-20 h-20 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all border-2 border-white/30 hover:scale-110"
            >
              <Minus className="w-10 h-10" />
            </button>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl border-2 border-white/30 px-12 py-8 min-w-[280px]">
              <div className="text-center">
                <div className="text-7xl font-bold mb-2">{guestCount}</div>
                <div className="flex items-center justify-center gap-2">
                  <button 
                    onClick={() => updateGuestCount(guestCount - 1)}
                    className="p-2 hover:bg-white/20 rounded transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => updateGuestCount(guestCount + 1)}
                    className="p-2 hover:bg-white/20 rounded transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <button 
              onClick={() => updateGuestCount(guestCount + 10)}
              className="w-20 h-20 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all border-2 border-white/30 hover:scale-110"
            >
              <Plus className="w-10 h-10" />
            </button>
          </div>
          
          <div className="flex justify-center gap-4 mt-8">
            <Button onClick={() => updateGuestCount(250)} variant="outline" className="bg-white/20 hover:bg-white/30 border-white/30 text-white">
              Přednastavit 250 osob
            </Button>
            <Button onClick={() => updateGuestCount(450)} variant="outline" className="bg-white/20 hover:bg-white/30 border-white/30 text-white">
              Přednastavit 450 osob
            </Button>
          </div>

          {/* Volume Discount Indicator */}
          <div className="max-w-2xl mx-auto mt-8" data-testid="volume-discount-indicator">
            {volumeDiscountPercent > 0 ? (
              <div className="bg-green-500/20 border-2 border-green-400/50 rounded-2xl p-5 text-center backdrop-blur-sm">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl">🎉</span>
                  <span className="text-lg md:text-xl font-bold text-green-300">
                    Aktivní objemová sleva {Math.round(volumeDiscountPercent * 1000) / 10} %
                  </span>
                </div>
                <p className="text-sm text-green-100/90">
                  Sleva se uplatňuje na <strong>jídlo a nápoje</strong>. Je <strong>progresivní</strong> –
                  od 251 hostů roste lineárně až na <strong>10 %</strong> při 450 hostech.
                </p>
                {guestCount < 450 && (
                  <p className="text-xs text-green-200/80 mt-2">
                    + {450 - guestCount} hostů → maximální sleva 10 %
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-white/10 border border-white/20 rounded-2xl p-5 text-center backdrop-blur-sm">
                <p className="text-base text-white">
                  💡 <strong>Tip:</strong> Objemová sleva na jídlo a nápoje startuje od <strong>251 hostů</strong>{' '}
                  a roste progresivně až do <strong>10 %</strong> při 450 hostech.
                </p>
                <p className="text-xs text-red-100/80 mt-1">
                  Pro aktivaci slevy přidejte alespoň {251 - guestCount > 0 ? 251 - guestCount : 1} {(251 - guestCount) === 1 ? 'hosta' : 'hostů'}.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <CateringCalculator catering={catering} updateCatering={updateCatering} prices={cateringPrices} total={cateringTotal} />
      <DrinksCalculator drinks={drinks} updateDrinks={updateDrinks} prices={drinksPrices} total={drinksTotal} />
      <InventorySection inventory={inventory} updateInventory={updateInventory} prices={inventoryPrices} />
      <OverheadsAndPersonnelSection 
        overheads={overheads} 
        personnel={personnel} 
        overheadsPrices={OVERHEADS_PRICES}
        overheadsTotal={overheadsTotal}
        personnelTotal={personnelTotal}
      />
      <BudgetSummary 
        catering={cateringTotal}
        drinks={drinksTotal}
        foodDrinksSubtotal={foodDrinksSubtotal}
        volumeDiscountPercent={volumeDiscountPercent}
        volumeDiscountAmount={volumeDiscountAmount}
        foodDrinksTotalAfterDiscount={foodDrinksTotalAfterDiscount}
        overheads={overheadsTotal}
        personnel={personnelTotal}
        program={PROGRAM}
        production={PRODUCTION}
        inventoryTotal={inventoryTotal}
        inventoryDiscount={inventoryDiscount}
        inventorySubtotal={inventorySubtotal}
        grandTotal={grandTotal}
        grandTotalBeforePartner={grandTotalBeforePartner}
        partnerDiscount={partnerDiscount}
        partnerDiscountActive={partnerDiscountActive}
        partnerDiscountThreshold={PARTNER_DISCOUNT_THRESHOLD}
        partnerDiscountAmount={PARTNER_DISCOUNT_AMOUNT}
        guestCount={guestCount}
        handleDownloadPDF={handleDownloadPDF}
      />
      <ContactSection />
    </div>
  );
};

// Hero Section
const HeroSection = ({ guestCount }) => (
  <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-red-900 to-black">
    <div className="absolute inset-0 z-0">
      <img 
        src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ygivb60k_Sni%CC%81mek%20obrazovky%202026-03-24%20v%C2%A019.02.44.png"
        alt="Greiner event"
        className="w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-red-900/40 to-black/80" />
    </div>

    <div className="relative z-20 max-w-6xl mx-auto px-6 md:px-12 py-24 text-center">
      {/* Logo */}
      <div className="mb-8 flex justify-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-6 border border-white/20">
          <img 
            src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/sj0u6am9_greiner-packaging_RGB.png"
            alt="Greiner packaging"
            className="h-16 md:h-20 w-auto"
          />
        </div>
      </div>

      {/* Title */}
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Greiner Letní Žúrka
        </h1>
        <p className="text-2xl md:text-3xl text-red-300 mb-3 font-light">
          Neformální akce pro zaměstnance firmy
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
          <Calendar className="w-8 h-8 text-red-400 mb-3 mx-auto" />
          <div className="text-3xl font-bold text-white mb-1">19.6.</div>
          <div className="text-red-200 text-sm">2026</div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
          <Clock className="w-8 h-8 text-red-400 mb-3 mx-auto" />
          <div className="text-2xl font-bold text-white mb-1">15-23h</div>
          <div className="text-red-200 text-sm">8 hodin</div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
          <Users className="w-8 h-8 text-red-400 mb-3 mx-auto" />
          <div className="text-3xl font-bold text-white mb-1">{guestCount}</div>
          <div className="text-red-200 text-sm">Hostů</div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
          <MapPin className="w-8 h-8 text-red-400 mb-3 mx-auto" />
          <div className="text-xl font-bold text-white mb-1">Liptál</div>
          <div className="text-red-200 text-sm">Venkovní areál</div>
        </div>
      </div>
    </div>
  </section>
);

// Info Section
const InfoSection = () => (
  <section className="py-16 px-4 bg-white">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-slate-800 mb-4">Kompletní zajištění akce</h2>
        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
          Program: DJ, AI fotkoutek, chytrý kvíz | Kompletní personální zajištění akce
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-2xl border border-red-100 shadow-lg hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6 mx-auto">
            <Utensils className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold mb-3 text-center text-slate-800">Street food catering</h3>
          <p className="text-slate-600 text-center">Food trucky, grilované speciality a live cooking</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-2xl border border-red-100 shadow-lg hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6 mx-auto">
            <Music className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold mb-3 text-center text-slate-800">DJ & Zábava</h3>
          <p className="text-slate-600 text-center">Profesionální DJ, chytrý kvíz a AI fotkoutek</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-2xl border border-red-100 shadow-lg hover:shadow-xl transition-shadow">
          <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mb-6 mx-auto">
            <Sparkles className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold mb-3 text-center text-slate-800">Komplexní servis</h3>
          <p className="text-slate-600 text-center">Od cateringu až po technické zajištění</p>
        </div>
      </div>
    </div>
  </section>
);

// Program Section
const ProgramSection = () => (
  <section className="py-16 px-4 bg-slate-50">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-6 py-3 rounded-full mb-6">
          <PartyPopper className="w-5 h-5" />
          <span className="font-bold text-lg">Program akce</span>
        </div>
        <h2 className="text-4xl font-bold text-slate-800 mb-4">DJ, AI fotkoutek & chytrý kvíz</h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-16">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-red-100 hover:shadow-xl transition-all hover:-translate-y-2">
          <div className="bg-gradient-to-br from-red-600 to-red-700 p-6 text-white">
            <Music className="w-12 h-12 mb-4" />
            <h3 className="text-2xl font-bold mb-2">DJ ZAX</h3>
            <div className="text-3xl font-bold">10 000 Kč</div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 mb-4">Profesionální DJ s mnohaletými zkušenostmi.</p>
            <div className="flex items-center text-sm text-slate-500">
              <Clock className="w-4 h-4 mr-2" />
              <span>Celá délka akce (15:00 - 23:00)</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-red-100 hover:shadow-xl transition-all hover:-translate-y-2">
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-6 text-white">
            <Sparkles className="w-12 h-12 mb-4" />
            <h3 className="text-2xl font-bold mb-2">Chytrý kvíz</h3>
            <div className="text-3xl font-bold">21 000 Kč</div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 mb-4">Interaktivní týmový kvíz mimo pódium.</p>
            <div className="text-sm text-slate-500 mb-4 space-y-1">
              <div className="font-semibold text-slate-700">Celková cena je odhadní</div>
              <div>Cena 200 Kč/osobu, která se do kvízu zapojí</div>
              <div>• Minimálně 7 000 Kč + cestovní náklady</div>
            </div>
            <div className="flex items-center text-sm text-slate-500">
              <Users className="w-4 h-4 mr-2" />
              <span>Mimo pódium - samostatná zóna</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-red-100 hover:shadow-xl transition-all hover:-translate-y-2">
          <div className="bg-gradient-to-br from-red-600 to-red-700 p-6 text-white">
            <Camera className="w-12 h-12 mb-4" />
            <h3 className="text-2xl font-bold mb-2">AI Fotkoutek</h3>
            <div className="text-3xl font-bold">20 000 Kč</div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 mb-4">AI fotokoutek s okamžitým tiskem a digitálním sdílením.</p>
            <div className="text-sm text-slate-500 space-y-2">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-red-600" />
                <span>3 hodiny provozu</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-red-600" />
                <span>19. 6. 2026 • 17:00 – 20:00</span>
              </div>
              <div className="flex items-center">
                <Camera className="w-4 h-4 mr-2 text-red-600" />
                <span>Neomezený počet fotek</span>
              </div>
              <div className="flex items-center text-red-700 font-semibold">
                <Sparkles className="w-4 h-4 mr-2" />
                <span>V ceně: zastřešení 8×4 m</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg">
        <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">Videoreportáž z loňské akce</h3>
        <div className="flex justify-center">
          <iframe 
            src="https://www.facebook.com/plugins/video.php?height=800&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F636556106069265%2F&show_text=false&width=450&t=0" 
            width="450" 
            height="800" 
            style={{border:'none', overflow:'hidden'}} 
            scrolling="no" 
            frameBorder="0" 
            allowFullScreen={true} 
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          />
        </div>
      </div>
    </div>
  </section>
);

// Catering Calculator
const CateringCalculator = ({ catering, updateCatering, prices, total }) => {
  const FoodItem = ({ id, label, emoji, price }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-red-300 transition-colors">
      <div className="flex items-center gap-3 flex-1">
        <span className="text-2xl">{emoji}</span>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">{label}</div>
          <div className="text-sm text-slate-500">{price} Kč/kus</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200">
          <button 
            onClick={() => updateCatering(id, catering[id] - 1)}
            className="p-2 hover:bg-red-100 rounded-l-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={catering[id] === 0}
          >
            <Minus className="w-5 h-5 text-red-600" />
          </button>
          <span className="px-4 py-2 min-w-[60px] text-center font-bold text-slate-800">{catering[id]}</span>
          <button 
            onClick={() => updateCatering(id, catering[id] + 1)}
            className="p-2 hover:bg-red-100 rounded-r-lg transition-colors"
          >
            <Plus className="w-5 h-5 text-red-600" />
          </button>
        </div>
        <div className="text-right min-w-[120px]">
          <div className="font-bold text-slate-800 text-lg">{(catering[id] * prices[id]).toLocaleString('cs-CZ')} Kč</div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Kalkulátor pokrmů</h2>
          <p className="text-lg text-slate-600">Nastavte počet porcí podle vašich potřeb</p>
          <p className="text-sm text-slate-500 mt-1 italic">Všechny ceny jsou uvedeny bez DPH</p>
        </div>

        <div className="space-y-8">
          {/* Hlavní jídla */}
          <div className="bg-red-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-red-800 mb-4">🍔 Grilování z foodtrucku <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="beefBurger" label="Beef burger" emoji="🍔" price={prices.beefBurger} />
              <FoodItem id="pulledHotdog" label="Pulled hot-dog" emoji="🌭" price={prices.pulledHotdog} />
              <FoodItem id="tortilla" label="Tortila se zeleninou" emoji="🌯" price={prices.tortilla} />
              <FoodItem id="krkovicka" label="Grilovaná krkovička" emoji="🥩" price={prices.krkovicka} />
              <FoodItem id="kureciPrso" label="Grilované kuřecí prso" emoji="🍗" price={prices.kureciPrso} />
              <FoodItem id="zebirka" label="Vepřové žebra BBQ" emoji="🍖" price={prices.zebirka} />
              <FoodItem id="hermelin" label="Grilovaný hermelín" emoji="🧀" price={prices.hermelin} />
            </div>
          </div>

          {/* Omáčky */}
          <div className="bg-amber-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-amber-800 mb-4">🥫 Omáčky k masu <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="peperovaOmacka" label="Pepřová omáčka" emoji="🌶️" price={prices.peperovaOmacka} />
              <FoodItem id="houbovaOmacka" label="Houbová omáčka" emoji="🍄" price={prices.houbovaOmacka} />
              <FoodItem id="tatarskaOmacka" label="Tatarská omáčka" emoji="🥫" price={prices.tatarskaOmacka} />
            </div>
          </div>

          {/* Přílohy */}
          <div className="bg-green-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-green-800 mb-4">🥔 Přílohy <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="zeleneLusky" label="Pečené zelené lusky" emoji="🫛" price={prices.zeleneLusky} />
              <FoodItem id="brambory" label="Pečené brambory grenaille" emoji="🥔" price={prices.brambory} />
              <FoodItem id="zelenina" label="Grilovaná zelenina" emoji="🥕" price={prices.zelenina} />
              <FoodItem id="kukurice" label="Grilovaná kukuřice" emoji="🌽" price={prices.kukurice} />
            </div>
          </div>

          {/* Studený raut */}
          <div className="bg-blue-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-blue-800 mb-4">🥗 Studený raut <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="kureciRizecky" label="Kuřecí banketní řízečky" emoji="🍗" price={prices.kureciRizecky} />
              <FoodItem id="veprovyRizecky" label="Vepřové řízečky" emoji="🥩" price={prices.veprovyRizecky} />
              <FoodItem id="coleslaw" label="Zelný salát coleslaw" emoji="🥗" price={prices.coleslaw} />
              <FoodItem id="sopskySalat" label="Šopský salát" emoji="🥗" price={prices.sopskySalat} />
            </div>
          </div>

          {/* Doplňky */}
          <div className="bg-orange-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-orange-800 mb-4">🥖 Doplňky <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="pecivo" label="Koš čerstvého pečiva" emoji="🥖" price={prices.pecivo} />
              <FoodItem id="studeneOmacky" label="Studené omáčky" emoji="🥫" price={prices.studeneOmacky} />
              <FoodItem id="zeleninka" label="Sterilizovaná zelenina" emoji="🥒" price={prices.zeleninka} />
            </div>
          </div>

          {/* Dezerty */}
          <div className="bg-pink-50 rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-pink-800 mb-4">🍰 Dezerty <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <FoodItem id="dezerty" label="Dezerty v kelímku" emoji="🍰" price={prices.dezerty} />
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-8 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-8 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Celkem Catering</h3>
              <p className="text-red-100">Kompletní gastronomické zajištění</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{total.toLocaleString('cs-CZ')} Kč</div>
              <div className="text-red-100 text-sm mt-1">bez DPH</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Drinks Calculator
const DrinksCalculator = ({ drinks, updateDrinks, prices, total }) => {
  const DrinkItem = ({ id, label, emoji, price }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-red-300 transition-colors">
      <div className="flex items-center gap-3 flex-1">
        <span className="text-2xl">{emoji}</span>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">{label}</div>
          <div className="text-sm text-slate-500">{price} Kč/kus</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200">
          <button 
            onClick={() => updateDrinks(id, drinks[id] - 1)}
            className="p-2 hover:bg-red-100 rounded-l-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={drinks[id] === 0}
          >
            <Minus className="w-5 h-5 text-red-600" />
          </button>
          <span className="px-4 py-2 min-w-[60px] text-center font-bold text-slate-800">{drinks[id]}</span>
          <button 
            onClick={() => updateDrinks(id, drinks[id] + 1)}
            className="p-2 hover:bg-red-100 rounded-r-lg transition-colors"
          >
            <Plus className="w-5 h-5 text-red-600" />
          </button>
        </div>
        <div className="text-right min-w-[120px]">
          <div className="font-bold text-slate-800 text-lg">{(drinks[id] * prices[id]).toLocaleString('cs-CZ')} Kč</div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-16 px-4 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Kalkulátor nápojů</h2>
          <p className="text-lg text-slate-600">Nastavte počet porcí podle vašich potřeb</p>
          <p className="text-sm text-slate-500 mt-1 italic">Všechny ceny jsou uvedeny bez DPH</p>
          <div className="max-w-2xl mx-auto mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">⭐ Dotované nápoje:</span> přednastavené počty (prosecco 2 ks/os., koktejly 1 ks/os., víno 0,5 ks/os., …) lze libovolně upravit dle přání klienta.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">🍷 Alkoholické nápoje <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <DrinkItem id="prosecco" label="Točené prosecco" emoji="🍾" price={prices.prosecco} />
              <DrinkItem id="koktejly" label="Míchané koktejly" emoji="🍹" price={prices.koktejly} />
              <DrinkItem id="vino" label="Sudové víno" emoji="🍷" price={prices.vino} />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">🍺 Pivo <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <DrinkItem id="radegast" label="Pivo Radegast" emoji="🍺" price={prices.radegast} />
              <DrinkItem id="prazdroj" label="Pivo Prazdroj" emoji="🍺" price={prices.prazdroj} />
              <DrinkItem id="birell" label="Točený Birell" emoji="🍺" price={prices.birell} />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">🥤 Nealko & Ostatní <span className="text-sm font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
            <div className="space-y-3">
              <DrinkItem id="kofola" label="Točená kofola" emoji="🥤" price={prices.kofola} />
              <DrinkItem id="voda" label="Voda PET" emoji="💧" price={prices.voda} />
              <DrinkItem id="kava" label="Prémiová káva" emoji="☕" price={prices.kava} />
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-8 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-8 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Celkem Nápoje</h3>
              <p className="text-slate-300">Kompletní nápojový servis</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{total.toLocaleString('cs-CZ')} Kč</div>
              <div className="text-slate-300 text-sm mt-1">bez DPH</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Overheads and Personnel Section
const OverheadsAndPersonnelSection = ({ overheads, personnel, overheadsPrices, overheadsTotal, personnelTotal }) => {
  const overheadsLabels = {
    jednorObaly: { label: "Jednorázové obaly a příbory", emoji: "🍽️", unit: "ks" },
    foodTruck: { label: "Food truck pronájem", emoji: "🚚", unit: "ks" },
    mobilniBar: { label: "Bar Truck XXL", emoji: "🍸", unit: "ks" },
    beerTruck: { label: "Beer truck", emoji: "🍺", unit: "ks" },
    nuzkoveStany: { label: "Nůžkové stany 6x3m pro catering", emoji: "⛺", unit: "ks" },
    nivtecStoly: { label: "Zátěžové výdejní stoly", emoji: "🪑", unit: "ks" },
    dopravaCatering: { label: "Doprava catering", emoji: "🚛", unit: "ks" },
    dopravaPersonal: { label: "Doprava personál", emoji: "🚐", unit: "ks" },
    dopravaFoodTruck: { label: "Doprava food truck", emoji: "🚚", unit: "ks" },
    dopravaBeerBar: { label: "Doprava beer bar", emoji: "🚛", unit: "ks" },
    likvidaceOdpadu: { label: "Likvidace odpadu", emoji: "♻️", unit: "ks" },
    uklidoveProstredky: { label: "Nákup úklidových prostředků a jejich distribuce", emoji: "🧴", unit: "ks" },
    prebraniArealu: { label: "Přebrání a předání areálu (4× cesta Otrokovice–Liptál + čas odp. osoby)", emoji: "🔑", unit: "ks" },
    pronajemArealu: { label: "Pronájem areálu", emoji: "🏛️", unit: "ks" }
  };

  const personnelLabels = {
    kuchar: { label: "Kuchař", emoji: "👨‍🍳" },
    pomocnyKuchar: { label: "Pomocný kuchař", emoji: "👨‍🍳" },
    barmani: { label: "Barmani - výčepní", emoji: "🍹" },
    barmaniKoktejly: { label: "Barmani - koktejly", emoji: "🍸" },
    debaras: { label: "Debaras (úklid)", emoji: "🧹" },
    ostraha: { label: "Ostraha areálu", emoji: "🛡️" }
  };

  const combinedTotal = overheadsTotal + personnelTotal;

  return (
    <section className="py-16 px-4 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold text-lg">Režie a personální zajištění</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Provozní náklady akce</h2>
          <p className="text-lg text-slate-600">Přepočítáno proporcionálně podle počtu hostů</p>
          <p className="text-sm text-slate-500 mt-1 italic">Všechny ceny jsou uvedeny bez DPH</p>
        </div>

        {/* REŽIE */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-slate-800 mb-6">🏗️ Režijní náklady <span className="text-base font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.keys(overheads).map((key) => {
              const item = overheadsLabels[key];
              const quantity = overheads[key];
              const price = overheadsPrices[key];
              const totalPrice = quantity * price;

              return (
                <div key={key} className="bg-white p-6 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{item.emoji}</span>
                        <h4 className="text-lg font-bold text-slate-800">{item.label}</h4>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <div>Jednotková cena: <span className="font-semibold text-blue-600">{price.toLocaleString('cs-CZ')} Kč/{item.unit}</span></div>
                        <div>Množství: <span className="font-semibold">{quantity} {item.unit}</span></div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-slate-800">{totalPrice.toLocaleString('cs-CZ')} Kč</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Režie celkem */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-6 shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-xl font-bold mb-1">Celkem Režie</h4>
                <p className="text-blue-100 text-sm">Veškeré provozní náklady akce</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">{overheadsTotal.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-blue-100 text-xs mt-1">bez DPH</div>
              </div>
            </div>
          </div>
        </div>

        {/* PERSONÁL */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-slate-800 mb-6">👥 Personální zajištění <span className="text-base font-normal text-slate-500 ml-2">(ceny bez DPH)</span></h3>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {Object.keys(personnel).map((key) => {
              const item = personnelLabels[key];
              const person = personnel[key];
              const totalPrice = person.count * person.hours * person.rate;

              return (
                <div key={key} className="bg-white p-6 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{item.emoji}</span>
                        <h4 className="text-lg font-bold text-slate-800">{item.label}</h4>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <div>Počet osob: <span className="font-semibold">{person.count} os.</span></div>
                        <div>Odpracované hodiny: <span className="font-semibold">{person.hours} hod</span></div>
                        <div>Hodinová sazba: <span className="font-semibold text-blue-600">{person.rate} Kč/hod</span></div>
                        <div className="pt-1 text-xs text-slate-500">
                          Výpočet: {person.count} os. × {person.hours} hod × {person.rate} Kč
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-slate-800">{totalPrice.toLocaleString('cs-CZ')} Kč</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Personál celkem */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl p-6 shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-xl font-bold mb-1">Celkem Personál</h4>
                <p className="text-slate-300 text-sm">Veškeré personální náklady na akci</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">{personnelTotal.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-slate-300 text-xs mt-1">bez DPH</div>
              </div>
            </div>
          </div>
        </div>

        {/* CELKOVÝ SOUČET REŽIE + PERSONÁL */}
        <div className="bg-gradient-to-r from-blue-800 to-slate-900 text-white rounded-2xl p-8 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold mb-2">Celkem Režie + Personál</h3>
              <p className="text-blue-200">Kompletní provozní zajištění akce</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{combinedTotal.toLocaleString('cs-CZ')} Kč</div>
              <div className="text-blue-200 text-sm mt-1">bez DPH</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Inventory Section
const InventorySection = ({ inventory, updateInventory, prices }) => {
  const subtotal = 
    inventory.stan8x4 * prices.stan8x4 + 
    inventory.pivniSet * prices.pivniSet +
    inventory.doprava * prices.doprava +
    inventory.osvetleni * prices.osvetleni +
    (inventory.koktejlovyStul || 0) * prices.koktejlovyStul +
    (inventory.identifikacniPasky || 0) * prices.identifikacniPasky +
    inventory.montaz * prices.montaz +
    (inventory.ozvuceniPodium || 1) * prices.ozvuceniPodium +
    (inventory.technickyKviz || 1) * prices.technickyKviz;
  const discount = subtotal * 0.20;
  const total = subtotal - discount;
  
  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-6 py-3 rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold text-lg">Nabídka inventáře a mobiliáře</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Technické vybavení</h2>
          <p className="text-sm text-slate-500 mt-1 italic">Všechny ceny jsou uvedeny bez DPH • Sleva 20 % aplikována automaticky</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Stan 8x4m */}
          <div className="bg-gradient-to-r from-red-50 to-white p-6 rounded-xl border border-red-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🏕️ Stan 8×4m</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-red-600">{prices.stan8x4.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.stan8x4} ks</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{(inventory.stan8x4 * prices.stan8x4).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Pivní set */}
          <div className="bg-gradient-to-r from-amber-50 to-white p-6 rounded-xl border border-amber-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🪑 Pivní set</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-amber-600">{prices.pivniSet.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.pivniSet} ks</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{(inventory.pivniSet * prices.pivniSet).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Doprava */}
          <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🚚 Doprava</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-blue-600">{prices.doprava.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.doprava} ks</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{(inventory.doprava * prices.doprava).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Osvětlení */}
          <div className="bg-gradient-to-r from-yellow-50 to-white p-6 rounded-xl border border-yellow-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">💡 Osvětlení do stanů</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-yellow-600">{prices.osvetleni.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.osvetleni} ks</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{(inventory.osvetleni * prices.osvetleni).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Koktejlový stůl */}
          <div className="bg-gradient-to-r from-rose-50 to-white p-6 rounded-xl border border-rose-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🍸 Koktejlový stůl vč. potahu</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-rose-600">{prices.koktejlovyStul.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.koktejlovyStul || 0} ks</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{((inventory.koktejlovyStul || 0) * prices.koktejlovyStul).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Identifikační pásky */}
          <div className="bg-gradient-to-r from-cyan-50 to-white p-6 rounded-xl border border-cyan-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🎟️ Identifikační pásky na ruce</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-cyan-600">{prices.identifikacniPasky.toLocaleString('cs-CZ')} Kč/ks</div>
                <div className="text-sm text-slate-500">Počet: {inventory.identifikacniPasky || 0} ks <span className="text-cyan-600 font-semibold">(1 ks / host)</span></div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{((inventory.identifikacniPasky || 0) * prices.identifikacniPasky).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Ozvučení a osvětlení pódia */}
          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-xl border border-purple-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🎤 Ozvučení a osvětlení pódia</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-purple-600">{prices.ozvuceniPodium.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-sm text-slate-500">Fixní cena (× {inventory.ozvuceniPodium || 1})</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{((inventory.ozvuceniPodium || 1) * prices.ozvuceniPodium).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Technická realizace chytrého kvízu */}
          <div className="bg-gradient-to-r from-indigo-50 to-white p-6 rounded-xl border border-indigo-100 shadow-md">
            <h3 className="text-xl font-bold text-slate-800 mb-2">📺 Technická realizace chytrého kvízu</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-indigo-600">{prices.technickyKviz.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-sm text-slate-500">2× TV, mikrofony, ozvučení (× {inventory.technickyKviz || 1})</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{((inventory.technickyKviz || 1) * prices.technickyKviz).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>

          {/* Montáž/demontáž */}
          <div className="bg-gradient-to-r from-green-50 to-white p-6 rounded-xl border border-green-100 shadow-md col-span-full md:col-span-2">
            <h3 className="text-xl font-bold text-slate-800 mb-2">🔧 Montáž / Demontáž</h3>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-green-600">{prices.montaz.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-sm text-slate-500">Fixní cena (× {inventory.montaz})</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-slate-800">{(inventory.montaz * prices.montaz).toLocaleString('cs-CZ')} Kč</div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary with discount */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-8 shadow-xl">
          <div className="space-y-4">
            <div className="flex justify-between items-center text-lg">
              <span>Mezisoučet inventáře:</span>
              <span className="font-bold">{subtotal.toLocaleString('cs-CZ')} Kč</span>
            </div>
            <div className="flex justify-between items-center text-lg border-t border-white/20 pt-4">
              <span>Sleva 20%:</span>
              <span className="font-bold text-red-200">-{discount.toLocaleString('cs-CZ')} Kč</span>
            </div>
            <div className="flex justify-between items-center text-2xl font-bold border-t-2 border-white/40 pt-4">
              <span>Celkem Inventář:</span>
              <div className="text-right">
                <div>{total.toLocaleString('cs-CZ')} Kč</div>
                <div className="text-red-100 text-xs font-normal mt-1">bez DPH</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Budget Summary
const BudgetSummary = ({ 
  catering, 
  drinks, 
  foodDrinksSubtotal,
  volumeDiscountPercent,
  volumeDiscountAmount,
  foodDrinksTotalAfterDiscount,
  overheads, 
  personnel, 
  program, 
  production, 
  inventoryTotal, 
  inventoryDiscount, 
  inventorySubtotal, 
  grandTotal, 
  grandTotalBeforePartner,
  partnerDiscount,
  partnerDiscountActive,
  partnerDiscountThreshold,
  partnerDiscountAmount,
  guestCount,
  handleDownloadPDF 
}) => (
  <section className="py-16 px-4 bg-gradient-to-br from-slate-900 via-red-900 to-black text-white">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4">Celkový rozpočet</h2>
        <p className="text-slate-300 text-lg">Přehled všech nákladů</p>
      </div>

      {/* "Bez DPH" banner */}
      <div className="bg-amber-500/15 border-2 border-amber-400/40 rounded-2xl p-4 mb-6 text-center">
        <p className="text-base md:text-lg text-amber-100">
          <span className="font-bold">📌 Všechny ceny v této nabídce jsou uvedeny bez DPH.</span>{' '}
          <span className="text-amber-100/80 text-sm">Sazba DPH: jídlo 12 %, nápoje a služby 21 %.</span>
        </p>
      </div>

      <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 mb-8">
        <div className="space-y-4">
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Catering</span>
            <span className="text-xl font-bold">{catering.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Nápoje</span>
            <span className="text-xl font-bold">{drinks.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          {volumeDiscountAmount > 0 && (
            <>
              <div className="flex justify-between items-center pb-2">
                <span className="text-sm text-slate-300">Mezisoučet jídlo + nápoje</span>
                <span className="text-sm text-slate-300">{foodDrinksSubtotal.toLocaleString('cs-CZ')} Kč</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-white/20">
                <span className="text-lg text-green-400">Objemová sleva ({Math.round(volumeDiscountPercent * 100)}% pro {guestCount} osob)</span>
                <span className="text-xl font-bold text-green-400">-{volumeDiscountAmount.toLocaleString('cs-CZ')} Kč</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-white/20 bg-green-500/10 -mx-4 px-4 py-2 rounded">
                <span className="text-lg font-semibold">Celkem jídlo + nápoje po slevě</span>
                <span className="text-xl font-bold text-green-400">{foodDrinksTotalAfterDiscount.toLocaleString('cs-CZ')} Kč</span>
              </div>
            </>
          )}
          
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Režie a technické vybavení</span>
            <span className="text-xl font-bold">{overheads.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Personál</span>
            <span className="text-xl font-bold">{personnel.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Program (DJ ZAX, Chytrý kvíz, AI Fotkoutek)</span>
            <span className="text-xl font-bold">{program.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          <div className="flex justify-between items-center pb-4 border-b border-white/20">
            <span className="text-lg">Produkce a koordinace</span>
            <span className="text-xl font-bold">{production.toLocaleString('cs-CZ')} Kč</span>
          </div>
          
          {inventoryTotal > 0 && (
            <div className="pb-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <span className="text-lg">Inventář (volitelné)</span>
                <span className="text-xl font-bold text-red-300">{inventoryTotal.toLocaleString('cs-CZ')} Kč</span>
              </div>
              {inventoryDiscount > 0 && (
                <div className="mt-3 ml-2 pl-3 border-l-2 border-green-400/40 space-y-1">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">Mezisoučet inventáře (před slevou)</span>
                    <span className="text-slate-300">{inventorySubtotal.toLocaleString('cs-CZ')} Kč</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-green-300 font-semibold">🎁 Sleva 20 % na technické vybavení</span>
                    <span className="text-green-300 font-semibold">-{inventoryDiscount.toLocaleString('cs-CZ')} Kč</span>
                  </div>
                  <div className="text-[11px] text-green-200/80 italic pt-1">
                    Sleva je automaticky aplikována na celé technické vybavení (stany, ozvučení, osvětlení, montáž apod.)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Partner Discount Indicator */}
          {partnerDiscountActive ? (
            <>
              <div className="flex justify-between items-center text-base text-slate-300 pt-2">
                <span>Mezisoučet (před partnerskou slevou)</span>
                <span>{grandTotalBeforePartner.toLocaleString('cs-CZ')} Kč</span>
              </div>
              <div className="flex justify-between items-center pb-2 bg-green-500/15 -mx-4 px-4 py-3 rounded border border-green-400/30">
                <div>
                  <div className="text-lg font-bold text-green-300">🤝 Partnerská sleva</div>
                  <div className="text-xs text-green-200/80">aktivní od {partnerDiscountThreshold} osob</div>
                </div>
                <span className="text-xl font-bold text-green-300">-{partnerDiscount.toLocaleString('cs-CZ')} Kč</span>
              </div>
            </>
          ) : (
            <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-3 text-sm text-amber-100 text-center">
              💡 <strong>Partnerská sleva {partnerDiscountAmount.toLocaleString('cs-CZ')} Kč</strong> se aktivuje při {partnerDiscountThreshold}+ hostech ({partnerDiscountThreshold - guestCount} {(partnerDiscountThreshold - guestCount) === 1 ? 'host' : 'hostů'} chybí).
            </div>
          )}
          
          <div className="flex justify-between items-center pt-6 text-2xl">
            <span className="font-bold">CELKEM bez DPH</span>
            <span className="font-bold text-red-300">{grandTotal.toLocaleString('cs-CZ')} Kč</span>
          </div>

          {/* VAT Breakdown */}
          {(() => {
            // Food = 12 % VAT (catering po objemové slevě)
            // Drinks po slevě + režie + personál + program + produkce + inventář = 21 % VAT
            // Partnerská sleva se odečítá ze základu 21 % (uplatněna na služby).
            const cateringAfterDiscount = catering * (1 - volumeDiscountPercent);
            const drinksAfterDiscount = drinks * (1 - volumeDiscountPercent);
            const base12 = cateringAfterDiscount;
            const base21Items = [
              { label: 'Nápoje (po slevě)', value: drinksAfterDiscount },
              { label: 'Režie a tech. vybavení', value: overheads },
              { label: 'Personál', value: personnel },
              { label: 'Program', value: program },
              { label: 'Produkce a koordinace', value: production },
              ...(inventoryTotal > 0 ? [{ label: 'Inventář', value: inventoryTotal }] : []),
              ...(partnerDiscountActive ? [{ label: '🤝 Partnerská sleva', value: -partnerDiscount }] : []),
            ];
            const base21 = base21Items.reduce((s, i) => s + i.value, 0);
            const vat12 = base12 * 0.12;
            const vat21 = base21 * 0.21;
            const totalVat = vat12 + vat21;
            const grandTotalWithVat = grandTotal + totalVat;
            const perPersonNoVat = guestCount > 0 ? grandTotal / guestCount : 0;
            const perPersonWithVat = guestCount > 0 ? grandTotalWithVat / guestCount : 0;
            const fmt = (v) => Math.round(v).toLocaleString('cs-CZ') + ' Kč';

            return (
              <div className="mt-6 pt-6 border-t border-white/20 space-y-3">
                <div className="text-sm text-slate-400 mb-2">Rozpad DPH</div>

                <div className="flex justify-between items-center text-base">
                  <span className="text-slate-300">Jídlo (základ DPH 12 %)</span>
                  <span className="text-slate-200">{fmt(base12)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400 pl-4">DPH 12 %</span>
                  <span className="text-slate-300">+ {fmt(vat12)}</span>
                </div>

                <div className="flex justify-between items-center text-base pt-3 border-t border-white/10">
                  <span className="text-slate-300">Nápoje a služby (základ DPH 21 %)</span>
                  <span className="text-slate-200">{fmt(base21)}</span>
                </div>
                <div className="space-y-1 pl-4 text-sm">
                  {base21Items.map((it) => (
                    <div key={it.label} className="flex justify-between items-center">
                      <span className="text-slate-400">• {it.label}</span>
                      <span className="text-slate-400">{fmt(it.value)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400 pl-4">DPH 21 %</span>
                  <span className="text-slate-300">+ {fmt(vat21)}</span>
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-white/10 text-base">
                  <span className="text-slate-300">DPH celkem</span>
                  <span className="text-slate-200 font-semibold">{fmt(totalVat)}</span>
                </div>

                <div className="flex justify-between items-center pt-3 text-2xl bg-red-500/10 -mx-4 px-4 py-3 rounded">
                  <span className="font-bold text-white">CELKEM s DPH</span>
                  <span className="font-bold text-red-300">{fmt(grandTotalWithVat)}</span>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4 mt-2 border-t border-white/10">
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                      Cena na osobu (bez DPH)
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-white" data-testid="per-person-no-vat">
                      {fmt(perPersonNoVat)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{guestCount} hostů</div>
                  </div>
                  <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-4 text-center">
                    <div className="text-xs uppercase tracking-wider text-red-200/80 mb-1">
                      Cena na osobu (s DPH)
                    </div>
                    <div className="text-xl md:text-2xl font-bold text-red-300" data-testid="per-person-with-vat">
                      {fmt(perPersonWithVat)}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">{guestCount} hostů</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="text-center">
        <Button 
          onClick={handleDownloadPDF}
          className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all"
        >
          <Download className="w-6 h-6 mr-3" />
          Stáhnout kompletní nabídku PDF
        </Button>
        <p className="mt-4 text-slate-400 text-sm">Uvedené ceny jsou bez DPH (jídlo 12 %, nápoje a služby 21 %)</p>
      </div>
    </div>
  </section>
);

// Contact Section
const ContactSection = () => (
  <section className="relative py-24 md:py-32 bg-gradient-to-br from-slate-900 via-red-900 to-black">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
      <div className="text-center">
        {/* Why Work With Us Section */}
        <div className="max-w-4xl mx-auto">
          <h2 
            className="font-serif text-4xl md:text-5xl text-slate-100 mb-12 tracking-tight text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Proč organizovat event s námi?
          </h2>
          
          <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
            <p 
              className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <strong className="text-white">Nejsme pouze produkční agentura.</strong> Jsme realizační společnost s vlastním technickým i cateringovým zázemím a týmem zkušených profesionálů – od kuchařů a techniků až po produkční a logistiku.
            </p>
            
            <p 
              className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Díky vlastnímu vybavení a internímu týmu máme <strong className="text-white">celý event plně pod kontrolou</strong>. Nejsme závislí na externích dodavatelích, což nám umožňuje garantovat kvalitu, přesné časování i transparentní rozpočet bez zbytečných provizí.
            </p>
            
            <p 
              className="text-lg md:text-xl text-slate-300 leading-relaxed text-justify"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Klient tak získává partnera, který má realizaci skutečně ve vlastních rukou – <strong className="text-white">od prvního návrhu až po poslední detail akce</strong>.
            </p>
            
            {/* Personal Guarantee */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <p 
                className="text-xl md:text-2xl text-red-400 font-semibold mb-8 text-center italic"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Za kvalitu i průběh realizace osobně ručím.
              </p>
              
              <div className="flex flex-col items-center gap-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/30q2xpfm_podpismore.png" 
                  alt="Podpis Jakuba Vodárka" 
                  className="h-24 md:h-32 object-contain"
                />
                <div className="text-center">
                  <p 
                    className="text-lg text-white font-semibold"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Jakub Vodárek
                  </p>
                  <p 
                    className="text-slate-400"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Jednatel společnosti
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          <div className="bg-red-900/40 backdrop-blur-xl border border-red-500/30 p-8 rounded-2xl hover:bg-red-900/60 transition-all">
            <Phone className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Telefon</h3>
            <a href="tel:+420773674222" className="text-red-300 hover:text-red-200 text-lg font-semibold transition-colors">
              +420 773 674 222
            </a>
          </div>
          
          <div className="bg-red-900/40 backdrop-blur-xl border border-red-500/30 p-8 rounded-2xl hover:bg-red-900/60 transition-all">
            <Mail className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Email</h3>
            <a href="mailto:info@vseproakci.cz" className="text-red-300 hover:text-red-200 text-lg font-semibold transition-colors">
              info@vseproakci.cz
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default GreinerLiptalOfferPage;
